import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
  Spinner,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchHumanCategories,
  fetchTranslation,
  addHumanCategory,
  updateHumanCategory,
  updateUserPreference,
  deleteHumanCategory,
  activateDeactivateHumanCategory,
  fetchHumanCategoriesTableColumnNames,
} from "../../../slices/thunks";
import { getAuthUser } from "../../../helpers/utils";
import ColumnSelectionModal from "../../../Components/Common/ColumnSelectionModal/ColumnSelectionModal";
import DeleteModal from "../../../Components/Common/DeleteModal";
import CommonAddModal from "../../../Components/Common/Modal/CommonAddModal";
import CommonDataTable from "../../../Components/Common/Table/CommonDataTable";
import usePermissions from "../../../Components/Hooks/usePermissions";
import ConfirmationModal from "../../../Components/Common/ConfirmationModal";
import HumanCategoriesForm from "../../../Components/Common/HumanCategories/HumanCategoriesForm";
import { toast } from "react-toastify";

const labels = [
  {
    id: 1,
    labelName: "TitleArabic",
    fieldName: "text-box-translate",
    name: "TitleAr",
    value: "TitleAr",
    required: true,
    minLength:3,
    minLengthErrorMessage: "TitleMinErrMessage",
    maxLength: 150,
    maxLengthErrorMessage: "TitleMaxErrMessage",
    errorMessage: "TitleRequired",
    translateTo: "TitleEn",
  },
  {
    id: 2,
    labelName: "TitleEnglish",
    fieldName: "text-box",
    name: "TitleEn",
    value: "TitleEn",
    required: true,
    minLength:3,
    minLengthErrorMessage: "TitleMinErrMessage",
    maxLength: 150,
    maxLengthErrorMessage: "TitleMaxErrMessage",
    errorMessage: "TitleRequired",
  },
  {
    id: 3,
    labelName: "SortDescArabic",
    fieldName: "text-box-translate",
    name: "SubTitleAr",
    value: "SubTitleAr",
    required: true,
    minLength:3,
    minLengthErrorMessage: "SubTitleMinErrMessage",
    maxLength: 250,
    maxLengthErrorMessage: "SubTitleMaxErrMessage",
    errorMessage: "SubTitleRequired",
    translateTo: "SubTitleEn"
  },
  {
    id: 4,
    labelName: "SortDescEnglish",
    fieldName: "text-box",
    name: "SubTitleEn",
    value: "SubTitleEn",
    required: true,
    minLength:3,
    minLengthErrorMessage: "SubTitleMinErrMessage",
    maxLength: 250,
    maxLengthErrorMessage: "SubTitleMaxErrMessage",
    errorMessage: "SubTitleRequired",
  },
  {
    id: 5,
    labelName: "LightIcon",
    fieldName: "icon",
    name: "LightIcon",
    value: "LightIcon",
    required: true,
    errorMessage: "IconRequired",
    accept: "image/png, image/jpeg"
  },
  {
    id: 6,
    labelName: "DarkIcon",
    fieldName: "icon",
    name: "DarkIcon",
    value: "DarkIcon",
    required: true,
    errorMessage: "IconRequired",
    accept: "image/png, image/jpeg"
  }
];

const actionConfig = {
  edit: {
    icon: "ri-edit-2-line",
    tooltip: "Edit",
  },
  delete: {
    icon: "ri-delete-bin-2-line",
    tooltip: "Delete",
  },
};

function HumanServices() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const {user} = getAuthUser();
  const [show, setShow] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddOrEdit, setIsAddOrEdit] = useState("isAdd");
  const [selectedRow, setSelectedRow] = useState();
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [userToToggle, setUserToToggle] = useState(null);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [lastTranslatedField, setLastTranslatedField] = useState(null);

  const {humanCategories, loading, error, tableColumnNames, translationLoading } = useSelector(
    (state) => ({
      tableColumnNames: state.HumanCategories.tableColumnNames,
      loading: state.HumanCategories.loading,
      error: state.HumanCategories.error,
      humanCategories: state.HumanCategories.humanCategories,
      translationLoading: state.Module.translationLoading
    })
  );

  const onTranslateClickHandler = (targetField, textToTranslate, formikSetFieldValue, sourceFieldName) => {
    if (!textToTranslate || textToTranslate.trim() === "") {
    toast.error(t("EnterTextToTranslate"));
    return;
    }
    setLastTranslatedField(sourceFieldName); 

    dispatch(fetchTranslation({ LangText: textToTranslate }))
    .unwrap() 
    .then(translatedText => {
        formikSetFieldValue(targetField, translatedText);
    })
    .catch(error => {
        console.error("Translation error:", error);
        toast.error(t("TranslationFailed"));
    })
    .finally(() => {
        setLastTranslatedField(null); 
    });
  };


  const handleClassesClicks = (value) => {
    setShow(true);
    setIsAddOrEdit(value);
    setSelectedRow({});
  };

  const onEditClickHandler = (user, value) => {
    setSelectedRow(user);
    setIsAddOrEdit(value);
    setShow(true);
  };
    

  const onSubmitHandler = (values, { resetForm }) => {
    // Prepare the payload
    const categoriesObj = {
      TitleEn: values.TitleEn.trim(),
      TitleAr: values.TitleAr.trim(),
      SubTitleEn: values.SubTitleEn.trim(),
      SubTitleAr: values.SubTitleAr.trim(),
      Icon: values.DarkIcon,
      LightIcon: values.LightIcon,
      DarkIcon: values.DarkIcon
    };

    if (isAddOrEdit === "isEdit" && selectedRow) {
      categoriesObj["ID"] = selectedRow.ID; 
    }

    if (isAddOrEdit === "isAdd") {
      dispatch(addHumanCategory(categoriesObj));
    } else {
      dispatch(updateHumanCategory(categoriesObj));
    }
    resetForm();
    setShow(false);
  };

  const saveUserPreferences = async () => {
    const preferencesPayload = {
      ColumnIDs: tableColumnNames.reduce((acc, column) => {
        acc[column.ID] = selectedColumns.includes(column.ID);
        return acc;
      }, {}),
    };

    setModalOpen(false);
    try {
      await dispatch(updateUserPreference(preferencesPayload)).unwrap();
      await dispatch(
        fetchHumanCategoriesTableColumnNames({
          module: "HUMAN_CATEGORIES",
        })
      );

    } catch (error) {
      console.error("Error updating preferences:", error);
    }
  };

  const confirmDelete = () => {
    dispatch(deleteHumanCategory({ IDs: selectedIds }))
      .then(() => {
        setSelectedIds([]);
      })
      .catch((error) => {
        console.error("Error deleting:", error);
      });
    setDeleteModal(false);
  };

  const onActiveOrDeactiveChange = (userItem, e) => {
    setUserToToggle(userItem);
    setShowConfirmationModal(true);
  };

  const handleConfirmToggleStatus = async () => {
    if (userToToggle) {
      setToggleLoading(true);
      try {
        await dispatch(
          activateDeactivateHumanCategory({
            ID: userToToggle.ID,
          })
        ).unwrap(); 
      } catch (error) {
        console.error("Failed to toggle user status:", error);
        // Handle error (e.g., show a toast message)
      } finally {
        setToggleLoading(false);
        setShowConfirmationModal(false);
        setUserToToggle(null);
      }
    }
  };

  

  // useEffect(() => {
  //   dispatch(
  //     fetchHumanCategoriesTableColumnNames({
  //       module: "HUMAN_CATEGORIES",
  //     })
  //   );
  //   dispatch(fetchHumanCategories());
  // }, [dispatch]);

  // Compute permission
  const permission = (() => {
    return usePermissions(1, 12);
  })();


  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(
          fetchHumanCategoriesTableColumnNames({
            module: "HUMAN_CATEGORIES",
          })
        ).unwrap();
        await dispatch(fetchHumanCategories());
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, [dispatch]);

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-3">
          <Col>
            <BreadCrumb title={t("HumanCategories")} />
          </Col>
        </Row>

        <div className="table-responsive">
          <Card>
            <CardHeader>
                <Row className="g-4 ">
                  <Col className="col-sm-auto">
                    <div>
                      <Button
                        className="btn btn-soft-danger ms-2 me-2"
                        onClick={() => setDeleteModal(true)}
                        disabled={selectedIds.length === 0}
                      >
                        <i className="ri-delete-bin-2-line"></i>
                      </Button>
                      {permission.CanAdd && (
                        <Button
                          color="secondary"
                          className="add-btn me-1"
                          onClick={() => handleClassesClicks("isAdd")}
                          id="create-btn"
                          disabled={loading}
                        >
                          <i className="ri-add-line align-bottom me-1 ms-1"></i>
                          {t("AddNew")}
                        </Button>
                      )}
                    </div>
                  </Col>
                  <Col className="col-sm">
                    <div className="d-flex justify-content-sm-end">
                      <Button
                        className="btn btn-soft"
                        onClick={() => setModalOpen(true)}
                        disabled={loading}
                      >
                        <i className="ri-filter-3-line"></i>
                      </Button>
                      <div className="search-box ms-2 me-2">
                        <Input
                          type="text"
                          className="form-control search"
                          placeholder={t("Search")}
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </div>
                  </Col>
                </Row>
            </CardHeader>

            <CardBody>
              <div className="listjs-table" id="customerList">
             
                {loading ? (
                  <div className="d-flex justify-content-center align-items-center">
                    <Spinner
                      style={{ height: "3rem", width: "3rem" }}
                      className="me-2"
                    >
                      Loading...
                    </Spinner>
                  </div>
                ) : (
                  <div className="listjs-table" id="customerList">
                     <CommonDataTable
                      data={humanCategories}
                      tableColumnNames={tableColumnNames}
                      searchTerm={searchTerm}
                      onEditClickHandler={onEditClickHandler}
                      onActiveOrDeactiveChange={onActiveOrDeactiveChange}
                      setDeleteModal={setDeleteModal}
                      setSelectedIds={setSelectedIds}
                      actionConfig={actionConfig}
                      permission={permission}
                    />
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        </div>
      </Container>

      {/* Modal  */}
      <CommonAddModal
        show={show}
        setShow={setShow}
        onSubmitHandler={onSubmitHandler}
        labels={labels}
        isAddOrEdit={isAddOrEdit}
        selectedItemData={selectedRow}
        FormBody={HumanCategoriesForm}
        FormBodyProps={{ onTranslateClickHandler, translationLoading, lastTranslatedField }} 
        title={isAddOrEdit === "isAdd" ? t("AddNewHumanCategory") : t("EditHumanCategory")}
        size="xl"
      />

      {/* ColumnSelectionModal */}
      <ColumnSelectionModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(!modalOpen)}
        columns={tableColumnNames}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        handleSave={saveUserPreferences}
      />

      {/* Delete modal  */}
      <DeleteModal
        showDelete={deleteModal}
        setShowDelete={setDeleteModal}
        onDeleteClick={confirmDelete}
        setSelectedIds={setSelectedIds}
        // description={
        //   selectedIds.length > 1
        //     ? `Are you sure you want to delete ${selectedIds.length} selected items?`
        //     : `Are you sure you want to delete this record?`
        // }
        description={t("confirmDelete")}
      />

      {/* Confirmation Modal for Activate/Deactivate */}
      <ConfirmationModal
        show={showConfirmationModal}
        setShow={setShowConfirmationModal}
        onConfirmClick={handleConfirmToggleStatus}
        item={userToToggle}
        loading={toggleLoading}
      />
    </div>
  );
}

export default HumanServices;
